// src/pages/projects/AllIncomePage.jsx
import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { Card, CardContent } from '@/components/ui/card';
import { Loader, Download, ArrowLeft, Eye, FileText } from 'lucide-react';
import { toast } from '@/components/ui/use-toast';
import { formatCurrency } from '@/lib/app-utils';
import { accountingAPI } from '@/services/accounting';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription
} from '@/components/ui/dialog';

const AllIncomePage = () => {
  const navigate = useNavigate();
  const [incomeRecords, setIncomeRecords] = useState([]);
  const [loading, setLoading] = useState(true);

  // Viewer state (نفس فكرة AllExpenses)
  const [viewingFiles, setViewingFiles] = useState(false);
  const [currentFiles, setCurrentFiles] = useState([]);
  const [currentTitle, setCurrentTitle] = useState('');
  const [loadingFiles, setLoadingFiles] = useState(false);

  const parseAttachment = (attachment) => {
    if (!attachment) return [];
    try {
      const parsed =
        typeof attachment === 'string' ? JSON.parse(attachment) : attachment;
      if (Array.isArray(parsed)) return parsed;
      return [parsed];
    } catch {
      return [];
    }
  };

  const getFileUrlFromAttachment = (file) => {
    if (!file) return null;
    if (file.fileUrl) return file.fileUrl;
    if (file.data) return file.data;
    return null;
  };

  const openFilesViewer = (attachment, title) => {
    const files = parseAttachment(attachment);
    if (!files.length) {
      toast({
        title: 'No File',
        description: 'No attachment found for this entry.',
        variant: 'destructive'
      });
      return;
    }
    setCurrentFiles(files);
    setCurrentTitle(title || 'Attached Files');
    setViewingFiles(true);
  };

  const handleOpenSingleFile = (file) => {
    const url = getFileUrlFromAttachment(file);
    if (!url) {
      toast({
        title: 'File Error',
        description: 'Could not locate file.',
        variant: 'destructive'
      });
      return;
    }
    window.open(url, '_blank');
  };

  const loadIncome = async () => {
    try {
      setLoading(true);

const accRes = await accountingAPI.getAllAccounting({
  type: 'income',
  status: 'Approved',
  include_archived: 1
});

      const income = (accRes.data?.income || []).filter(
        (e) => (e.status || 'Approved') === 'Approved'
      );
      setIncomeRecords(income);
    } catch (error) {
      console.error(error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to load income data',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadIncome();
  }, []);

  const totalIncome = incomeRecords.reduce(
    (sum, e) => sum + (parseFloat(e.amount) || 0),
    0
  );

  const allIncome = incomeRecords.map((e) => ({
    projectId: e.projectId || e.project_id,
    projectName: e.projectName,
    companyName: e.companyName,
    description: e.description,
    category: e.category,
    amount: e.amount,
    date: e.date,
    status: e.status,
    attachment: e.attachment || null
  }));

  const handleExportExcel = async () => {
    if (allIncome.length === 0) {
      toast({
        title: 'No Data',
        description: 'No income records to export.',
        variant: 'destructive'
      });
      return;
    }

    const XLSX = await import('xlsx');

    const rows = allIncome.map((item) => ({
      Project: item.projectName,
      Company: item.companyName,
      Category: item.category,
      Description: item.description,
      'Amount (AED)': parseFloat(item.amount).toFixed(2),
      Date: new Date(item.date).toLocaleDateString(),
      Status: item.status
    }));

    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Income');
    XLSX.writeFile(
      wb,
      `All_Income_${new Date().toISOString().split('T')[0]}.xlsx`
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>All Income | Stone Beam</title>
      </Helmet>

      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold">All Income</h1>
            <p className="text-sm text-muted-foreground">
              Approved income entries across all projects
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate('/projects')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Projects
            </Button>
            <Button onClick={handleExportExcel}>
              <Download className="h-4 w-4 mr-2" />
              Export Excel
            </Button>
          </div>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card>
            <CardContent className="p-4">
              <p className="text-xs uppercase text-muted-foreground font-semibold">
                Total Approved Income
              </p>
              <p className="text-2xl font-bold text-green-700">
                {formatCurrency(totalIncome)}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Entries: {incomeRecords.length}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Project</TableHead>
                  <TableHead>Company</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="text-center">Files</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {allIncome.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={8}
                      className="text-center py-8 text-muted-foreground"
                    >
                      No income records found.
                    </TableCell>
                  </TableRow>
                ) : (
                  allIncome.map((item, idx) => (
                    <TableRow key={idx}>
                      <TableCell>
                        {item.projectId ? (
                          <button
                            className="text-blue-600 hover:underline"
                            onClick={() =>
                              navigate(`/projects/${item.projectId}`)
                            }
                          >
                            {item.projectName}
                          </button>
                        ) : (
                          item.projectName
                        )}
                      </TableCell>
                      <TableCell>{item.companyName}</TableCell>
                      <TableCell>{item.category}</TableCell>
                      <TableCell>{item.description}</TableCell>
                      <TableCell className="text-center">
                        {item.attachment ? (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            onClick={() =>
                              openFilesViewer(item.attachment, item.description)
                            }
                            title="View attached files"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        ) : (
                          <span className="text-xs text-muted-foreground">
                            -
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="font-semibold">
                        {formatCurrency(item.amount)}
                      </TableCell>
                      <TableCell>
                        {item.date
                          ? new Date(item.date).toLocaleDateString()
                          : '-'}
                      </TableCell>
                      <TableCell>{item.status}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>

      {/* Files Viewer Dialog (نفس ستايل AllExpenses) */}
      <Dialog open={viewingFiles} onOpenChange={setViewingFiles}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Attached Files</DialogTitle>
            {currentTitle && (
              <DialogDescription>{currentTitle}</DialogDescription>
            )}
          </DialogHeader>

          <div className="space-y-2 max-h-[400px] overflow-y-auto mt-2">
            {loadingFiles ? (
              <div className="flex justify-center py-8">
                <Loader className="h-6 w-6 animate-spin text-primary" />
              </div>
            ) : currentFiles.length === 0 ? (
              <div className="text-center py-6 text-muted-foreground text-sm">
                No files attached.
              </div>
            ) : (
              currentFiles.map((file, idx) => (
                <div
                  key={idx}
                  className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border hover:bg-slate-100 transition-colors"
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <FileText className="h-5 w-5 text-blue-600 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">
                        {file.fileName || file.name || `File ${idx + 1}`}
                      </p>
                      {file.fileSize && (
                        <p className="text-xs text-muted-foreground">
                          {file.fileSize}
                        </p>
                      )}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleOpenSingleFile(file)}
                  >
                    <Eye className="h-4 w-4 mr-1" />
                    View
                  </Button>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AllIncomePage;
